import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const AdminAccessProvisioningTab = ({ user }) => {
  const { tokenManager } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessItems, setAccessItems] = useState({
    erp: false,
    gmos: false,
    nonProjectExpense: false,
    asset_tracker: false,
  });
  const [saving, setSaving] = useState(false);

  // Access items configuration
  const accessConfig = [
    {
      key: 'erp',
      label: 'ERP System Access',
      description: 'Enterprise Resource Planning',
      icon: <BusinessIcon />,
      color: 'primary',
    },
    {
      key: 'gmos',
      label: 'GMOS Access',
      description: 'Marketplace for Online Sampling',
      icon: <SecurityIcon />,
      color: 'secondary',
    },
    {
      key: 'nonProjectExpense',
      label: 'Non Project Expense Access',
      description: 'Non Project Expense management',
      icon: <InventoryIcon />,
      color: 'success',
    },
     {
          key : 'asset_tracker',
          label : 'Asset Tracker',
          description : 'Asset Management System',
          icon : <PersonIcon />,
          color : 'warning',
      }
  ];

  useEffect(() => {
    fetchRequests();
    if (user) {
      fetchAccessProvisioning();
    }
  }, [user]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/access-provisioning/user/${user?._id || user?.id}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
      } else {
        // If no access provisioning endpoint exists, show placeholder
        setRequests([]);
      }
    } catch (error) {
      console.error('Error fetching access requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch access provisioning data from backend
  const fetchAccessProvisioning = async () => {
    if (!user) return;

    try {
      const response = await fetch(`${API_URL}/access-provisioning/${user._id || user.id}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setAccessItems({
            erp: result.data.erp || false,
            gmos: result.data.gmos || false,
            nonProjectExpense: result.data.nonProjectExpense || false,
            asset_tracker : result.data.asset_tracker || false,
          });
        }
      } else {
        console.error('Failed to fetch access provisioning data');
        // Set default values if no data exists
        setAccessItems({
          erp: false,
          gmos: false,
          nonProjectExpense: false,
          asset_tracker: false,
        });
      }
    } catch (error) {
      console.error('Error fetching access provisioning:', error);
      // Set default values on error
      setAccessItems({
        erp: false,
        gmos: false,
        nonProjectExpense: false,
        asset_tracker: false,
      });
    }
  };

  // Handle checkbox changes
  const handleAccessChange = (key) => (event) => {
    const newAccessItems = {
      ...accessItems,
      [key]: event.target.checked,
    };
    setAccessItems(newAccessItems);

    // Save to backend
    saveAccessProvisioning(newAccessItems);
  };

  // Save access provisioning to backend
  const saveAccessProvisioning = async (accessData) => {
    if (!user) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/access-provisioning`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
        body: JSON.stringify({
          employeeSelectId: user._id || user.id,
          erp: accessData.erp,
          gmos: accessData.gmos,
          nonProjectExpense: accessData.nonProjectExpense,
          asset_tracker: accessData.asset_tracker,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Access provisioning updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update access provisioning');
      }
    } catch (error) {
      console.error('Error saving access provisioning:', error);
      toast.error('Failed to save access provisioning');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {`${user?.firstName} ${user?.lastName}'s Access Provisioning` }
        </Typography>
       
      </Box>

      {/* User Details */}
      {user && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <PersonIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Employee Details
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Name:</strong> {user.firstName} {user.lastName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Email:</strong> {user.email}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Department:</strong> {user.department || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Job Title:</strong> {user.jobTitle || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Access Provisioning Section */}
      {user && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
              Access Provisioning Setup
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure system access for {user?.firstName || 'the employee'}. 
              Check the boxes to grant access to different systems.
            </Typography>

            {/* Loading and Saving Indicators */}
            {saving && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Saving changes...
              </Alert>
            )}

            {/* Access Items Grid */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {accessConfig.map((item) => (
                <Grid item xs={12} md={4} key={item.key}>
                  <Card 
                    elevation={accessItems[item.key] ? 3 : 1}
                    sx={{ 
                      border: accessItems[item.key] ? `2px solid ${item.color}` : '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      bgcolor: accessItems[item.key] ? `${item.color}.50` : 'white',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Box sx={{ color: `${item.color}.main` }}>
                          {item.icon}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {item.label}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {item.description}
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={accessItems[item.key]}
                            onChange={handleAccessChange(item.key)}
                            color={item.color}
                            size="large"
                            disabled={saving}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {accessItems[item.key] ? 'Access Granted' : 'Grant Access'}
                            </Typography>
                            {accessItems[item.key] && (
                              <Chip 
                                label="✓ Provisioned" 
                                size="small" 
                                color={item.color}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Access Request History - Only show if there are requests */}
      {requests.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Access Request History ({requests.length})
            </Typography>
            <List>
              {requests.map((request) => (
                <ListItem key={request._id || request.id} divider>
                  <ListItemText
                    primary={request.accessType || request.title}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {request.description || request.reason}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Requested: {new Date(request.createdAt || request.requestedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={request.status || 'Pending'} 
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                      <IconButton
                        edge="end"
                        aria-label="view"
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AdminAccessProvisioningTab;
